import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import type { Pagina } from '@/shared/api/cliente';

export const TAMANIOS_DE_PAGINA = [10, 25, 50, 100] as const;

export interface Columna<T> {
  clave: string;
  encabezado: string;
  alinear?: 'izquierda' | 'centro' | 'derecha';
  clase?: string;
  contenido: (fila: T, indice: number) => ReactNode;

  orden?: string;

  pie?: ReactNode;
}

export interface OrdenDeTabla {
  campo: string;
  descendente: boolean;
}

interface Props<T> {
  datos: Pagina<T> | undefined;
  cargando: boolean;
  columnas: Columna<T>[];
  tamanio: number;
  alCambiarPagina: (pagina: number) => void;
  alCambiarTamanio: (tamanio: number) => void;
  iconoVacio?: LucideIcon;
  mensajeVacio?: string;
  herramientas?: ReactNode;

  entidad?: string | undefined;

  paginado?: boolean;

  separador?: (fila: T, anterior: T | undefined) => ReactNode;

  orden?: OrdenDeTabla | null;

  alOrdenar?: (orden: OrdenDeTabla | null) => void;
}

const ALINEACION = {
  izquierda: 'text-left',
  centro: 'text-center',
  derecha: 'text-right',
} as const;

export function Tabla<T>({
  datos,
  cargando,
  columnas,
  tamanio,
  alCambiarPagina,
  alCambiarTamanio,
  iconoVacio: IconoVacio = FileText,
  mensajeVacio = 'No hay registros',
  herramientas,
  entidad,
  paginado = true,
  separador,
  orden = null,
  alOrdenar,
}: Props<T>) {
  const conPie = columnas.some((c) => c.pie !== undefined);

  function ordenar(campo: string) {
    if (!alOrdenar) return;
    if (orden?.campo !== campo) alOrdenar({ campo, descendente: false });
    else if (!orden.descendente) alOrdenar({ campo, descendente: true });
    else alOrdenar(null);
  }

  const paginaActual = (datos?.pagina ?? 0) + 1;
  const totalPaginas = datos?.totalPaginas ?? 1;
  const total = datos?.totalElementos ?? 0;
  const desde = total === 0 ? 0 : (datos?.pagina ?? 0) * (datos?.tamanio ?? tamanio) + 1;
  const hasta = desde + (datos?.contenido.length ?? 0) - 1;

  return (
    <div className="rounded-2xl border border-armazon-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 px-5 py-3">
        {paginado && (
        <select
          value={tamanio}
          onChange={(e) => alCambiarTamanio(Number(e.target.value))}
          aria-label="Registros por página"
          className="rounded-lg border border-armazon-200 bg-white px-2.5 py-1.5 text-sm text-armazon-700 outline-none transition-colors focus:border-marca-400 focus:ring-2 focus:ring-marca-500/20"
        >
          {TAMANIOS_DE_PAGINA.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        )}

        {herramientas && <div className="flex flex-1 items-center justify-end gap-3">{herramientas}</div>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-armazon-50/80">
            <tr>
              {columnas.map((c) => {
                const activa = c.orden !== undefined && orden?.campo === c.orden;
                return (
                  <th
                    key={c.clave}
                    scope="col"
                    aria-sort={
                      activa ? (orden?.descendente ? 'descending' : 'ascending') : undefined
                    }
                    className={[
                      'px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-armazon-500',
                      ALINEACION[c.alinear ?? 'izquierda'],
                      c.clase ?? '',
                    ].join(' ')}
                  >
                    {c.orden !== undefined && alOrdenar ? (
                      <button
                        type="button"
                        onClick={() => ordenar(c.orden as string)}
                        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-armazon-800 ${
                          activa ? 'text-marca-700' : ''
                        }`}
                      >
                        {c.encabezado}
                        {activa ? (
                          orden?.descendente ? (
                            <ArrowDown className="size-3" aria-hidden />
                          ) : (
                            <ArrowUp className="size-3" aria-hidden />
                          )
                        ) : (
                          <ArrowUpDown className="size-3 opacity-40" aria-hidden />
                        )}
                      </button>
                    ) : (
                      c.encabezado
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-armazon-100">
            {cargando ? (

              [...Array(Math.min(tamanio, 5))].map((_, i) => (
                <tr key={i}>
                  {columnas.map((c) => (
                    <td key={c.clave} className="px-3 py-3.5">
                      <div
                        className="h-3.5 animate-pulse rounded bg-armazon-100"
                        style={{ width: `${55 + ((i * 13) % 35)}%`, animationDelay: `${i * 80}ms` }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : !datos?.contenido.length ? (
              <tr>
                <td colSpan={columnas.length} className="px-5 py-16 text-center">
                  <IconoVacio className="mx-auto mb-3 size-10 text-armazon-200" aria-hidden />
                  <p className="text-sm font-medium uppercase text-armazon-400">{mensajeVacio}</p>
                </td>
              </tr>
            ) : (
              datos.contenido.flatMap((fila, i) => {
                const franja = separador?.(fila, i === 0 ? undefined : datos.contenido[i - 1]);

                return [
                  ...(franja
                    ? [
                        <tr key={`separador-${i}`} className="bg-armazon-50/70">
                          <td
                            colSpan={columnas.length}
                            className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-500"
                          >
                            {franja}
                          </td>
                        </tr>,
                      ]
                    : []),
                  <tr key={i} className="transition-colors hover:bg-armazon-50/60">
                    {columnas.map((c) => (
                      <td
                        key={c.clave}
                        className={`px-3 py-3 ${ALINEACION[c.alinear ?? 'izquierda']}`}
                      >
                        {c.contenido(fila, desde + i - 1)}
                      </td>
                    ))}
                  </tr>,
                ];
              })
            )}
          </tbody>

          {conPie && !cargando && !!datos?.contenido.length && (
            <tfoot className="border-t-2 border-armazon-200 bg-armazon-50/80">
              <tr>
                {columnas.map((c) => (
                  <td
                    key={c.clave}
                    className={`px-3 py-3 text-xs font-bold text-armazon-800 ${ALINEACION[c.alinear ?? 'izquierda']}`}
                  >
                    {c.pie}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {datos && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-t border-armazon-100 px-5 py-4">
          <p className="text-sm uppercase tracking-wide text-armazon-500">
            Mostrando <span className="font-semibold text-armazon-700">{desde}</span> a{' '}
            <span className="font-semibold text-armazon-700">{hasta}</span> de{' '}
            <span className="font-semibold text-armazon-700">{total}</span>
            {entidad ? ` ${entidad}` : ''}
          </p>

          <nav className="flex flex-wrap items-center justify-end gap-1" aria-label="Paginación">
              <BotonDePagina
                alPulsar={() => alCambiarPagina(0)}
                deshabilitado={paginaActual === 1}
                etiqueta="Primero"
              />
              <BotonDePagina
                alPulsar={() => alCambiarPagina(Math.max(0, paginaActual - 2))}
                deshabilitado={paginaActual === 1}
                aria="Página anterior"
              >

                <ChevronLeft className="size-4" aria-hidden />
                <span className="hidden sm:inline">Anterior</span>
              </BotonDePagina>

              {ventanaDePaginas(paginaActual, totalPaginas).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => alCambiarPagina(p - 1)}
                  aria-current={p === paginaActual ? 'page' : undefined}
                  className={[
                    'size-9 rounded-lg text-sm font-medium transition-colors',
                    p === paginaActual
                      ? 'bg-marca-600 text-white shadow-sm'
                      : 'border border-armazon-200 text-armazon-600 hover:bg-armazon-50',
                  ].join(' ')}
                >
                  {p}
                </button>
              ))}

              <BotonDePagina
                alPulsar={() => alCambiarPagina(Math.min(totalPaginas - 1, paginaActual))}
                deshabilitado={paginaActual === totalPaginas}
                aria="Página siguiente"
              >
                <span className="hidden sm:inline">Siguiente</span>
                <ChevronRight className="size-4" aria-hidden />
              </BotonDePagina>
              <BotonDePagina
                alPulsar={() => alCambiarPagina(totalPaginas - 1)}
                deshabilitado={paginaActual === totalPaginas}
                etiqueta="Último"
              />
          </nav>
        </div>
      )}
    </div>
  );
}

function BotonDePagina({
  alPulsar,
  deshabilitado,
  etiqueta,
  aria,
  children,
}: {
  alPulsar: () => void;
  deshabilitado: boolean;
  etiqueta?: string;
  aria?: string;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={deshabilitado}
      aria-label={aria ?? etiqueta}
      className={[
        'rounded-lg border border-armazon-200 text-sm text-armazon-600 transition-colors',
        'inline-flex items-center gap-1',
        'hover:bg-armazon-50 disabled:opacity-30 disabled:hover:bg-transparent',
        etiqueta ? 'px-3 py-1.5' : 'px-2 py-1.5',
      ].join(' ')}
    >
      {etiqueta ?? children}
    </button>
  );
}

function ventanaDePaginas(actual: number, total: number): number[] {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const inicio = actual <= 3 ? 1 : actual >= total - 2 ? total - 4 : actual - 2;
  return Array.from({ length: 5 }, (_, i) => inicio + i);
}
